/**
 * The doorman's standing prompts (FR-11) — the jobs a new owner has not done
 * yet, and the buttons that do them.
 *
 * THEY DO NOT SCROLL AWAY (owner, 2026-08-25). They used to be doorman messages
 * in the thread, one paragraph and one button each, which read well for exactly
 * as long as the thread was three lines long. The moment the owner had a real
 * conversation the jobs were somewhere up above it, and the only way back to a
 * call to action was to scroll for it. A call to action that has to be hunted
 * for is not one.
 *
 * So they moved out of the conversation and into the masthead, under the
 * briefing's title, where the whole column can move underneath them. That costs
 * them the doorman's prose — paragraphs pinned above a chat is a banner, not a
 * masthead — and the CTA is what survives, because the CTA is the part that was
 * being lost. The voice is still on the thread; this is the shelf.
 *
 * THERE ARE TWO OF THEM, NOT THREE (ruling R-0826-3). "Add your email" used to
 * lead the list and it is deleted along with its panel: the address is now a
 * required step INSIDE the onboarding run, proved by a code before the run
 * advances, so every owner who can see this shelf already has one. The prompt
 * would render as done on its first frame for ever — which is not a prompt, it
 * is a bug with a number beside it.
 *
 * A PROMPT IS ANSWERED BY ITS OWN ACTION COMPLETING. Opening the share sheet is
 * not answering it; walking to the rules page is not answering it. So nothing
 * here marks anything done locally — every action ends by re-reading the
 * thread, and the prompt stays lit until the SERVER stops sending it. One of
 * the two the server observes for itself (a rules version written); the other —
 * handing the door out — happens in a clipboard or in X's composer, where the
 * server can see nothing, so the share sheet reports it, and only ever after
 * somebody really copied something.
 *
 * THEY ARE ACCENT PILLS, NOT QUIET CHIPS (owner, 2026-08-26). Moving them to
 * the masthead fixed WHERE they were and not how loudly they asked: a row of
 * bordered grey chips in 11px is furniture, and an owner scrolling past it does
 * not read furniture. So the CTA takes the app's own primary shape — the accent
 * fill and ink type `Button` and the sidebar's `+` already wear — one step up
 * the type scale, and the minimised one-liner becomes accent-ink and semibold
 * so the thing that survives the collapse is still the thing being asked.
 *
 * ONE PER LINE, EACH WITH A LARGE CIRCLED NUMBER (owner, 2026-08-26). The pills
 * wrapped into a row, which read as tags rather than as jobs and put whichever
 * one happened to wrap last on a line of its own for no reason. They are a
 * numbered list now — an `<ol>`, because they really are ordered — with the
 * whole row clickable and a 28px counter on the left of it. The numeral is INK
 * ON THE ACCENT ROW INVERTED: the brief asked for a filled accent circle, and an
 * accent circle on an accent fill is not a circle. Inverting keeps the row loud
 * (the fill is what the owner asked for) and still gives the numeral a hard disc
 * to sit in.
 *
 * Every colour is a token, which is what makes it survive nob mode: `accent` is
 * the same yellow in all three themes and `accent-fg` the same ink, so the pill
 * cannot invert on anybody. What nob mode DOES need is an edge — its pane is
 * paper (#fffbed) and yellow-on-cream has no outline of its own — so the ink
 * border and hard shadow are added there, and only there, by `.nob-prompt-cta`
 * in `styles.css`. That is the shell's own way of doing it (`.nob-rail-do`),
 * not the poster's sticker class, which carries the poster's font and geometry.
 *
 * ONE BUTTON, ONE SURFACE ELSEWHERE (owner, 2026-08-25). These used to unfold
 * their own panels inside the thread — the share prompt in particular opened
 * the whole door-share section inline, which put a second, differently-shaped
 * copy of the share surface in the middle of a conversation. A prompt is the
 * doorman ASKING; the place the job gets done is the place it already lives, so
 * the buttons open the shell's own sheet and panels.
 */
import { useState } from "react";
import type { DoormanPrompt, PromptAction } from "../../lib/api";

export const PROMPT_BUTTON: Record<PromptAction, string> = {
  share_door: "Hand out your door",
  edit_rules: "Personalize your house rules",
};

/**
 * What the minimised masthead says instead of the list. A COUNT, not a nag: the
 * owner scrolling their thread is doing something else, and the only thing they
 * need from this row is the knowledge that the jobs are still there and where to
 * tap for them.
 */
export function promptCountLabel(n: number): string {
  return `${n} thing${n === 1 ? "" : "s"} to set up`;
}

type Props = {
  prompts: DoormanPrompt[];
  /**
   * Hand the door out — the shell's own sheet, the same one the desk and the
   * pane header open. Not a panel of this thread's own: there is one share
   * surface, and a second copy inside a conversation is how the two drift.
   */
  onShareDoor: () => void;
  /**
   * Personalize the rules. The caller decides HOW — a doorman that can do the
   * work is asked to; a Free one cannot, so the editor opens instead — because
   * the caller is the shell, which is the thing that knows the tier.
   *
   * `null` is the third answer: the tier is not known yet. The row is disabled
   * for that beat rather than guessing, because guessing Free is how a
   * subscriber's press landed in the editor on a cold load (staging walk,
   * 2026-09-09).
   */
  onEditRules: (() => void) | null;
  /**
   * The door exists and is PAUSED (owner, 2026-08-27) — the shelf's most urgent
   * row, because every other prompt on it assumes there is a door to set up and
   * a paused one makes them all moot. Pressing it opens the shell's one reopen
   * confirm.
   */
  paused?: boolean;
  onReopen?: (() => void) | undefined;
};

export const REOPEN_PROMPT_LABEL = "Get your door back online";

export function DoormanPrompts({
  prompts,
  onShareDoor,
  onEditRules,
  paused = false,
  onReopen,
}: Props) {
  /**
   * The owner tapped the minimised count open. Local, and deliberately NOT
   * remembered: the collapse follows the masthead's own state, and a scroll back
   * to the top restores the full list anyway.
   */
  const [expanded, setExpanded] = useState(false);

  const pending = prompts.filter((p) => !p.done);
  const reopenRow = paused && onReopen !== undefined;
  if (pending.length === 0 && !reopenRow) return null;

  return (
    <div
      className="nob-prompts mx-auto w-full max-w-2xl px-6"
      data-testid="doorman-prompts"
      data-expanded={expanded ? "true" : "false"}
    >
      {/* The minimised line. Hidden by CSS until the masthead itself minimises
          (`.nob-masthead[data-scrolled]`), so there is exactly one state
          machine here and it belongs to the scroller, not to this component. */}
      <button
        type="button"
        className="nob-prompts-count items-center gap-1.5 rounded-full text-sm font-semibold
                   text-accent-ink transition-colors duration-75 hover:text-accent-ink-strong
                   focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-focus/70 focus-visible:ring-offset-2
                   focus-visible:ring-offset-bg"
        aria-expanded={expanded}
        aria-controls="doorman-prompt-list"
        onClick={() => setExpanded((v) => !v)}
      >
        {promptCountLabel(pending.length + (reopenRow ? 1 : 0))}
        <span aria-hidden="true">{expanded ? "⌃" : "⌄"}</span>
      </button>

      <ol id="doorman-prompt-list" className="nob-prompts-list flex list-none flex-col gap-2">
        {reopenRow && (
          <li key="reopen">
            <button
              type="button"
              onClick={onReopen}
              className="nob-prompt-cta flex w-full items-center gap-3 rounded-2xl bg-accent
                         py-2 pl-2 pr-4 text-left text-sm font-semibold text-accent-fg
                         transition-colors duration-75 hover:bg-accent-soft active:bg-accent-strong
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-focus/70 focus-visible:ring-offset-2
                         focus-visible:ring-offset-bg"
            >
              <span aria-hidden="true" className="nob-prompt-num">
                !
              </span>
              <span className="min-w-0 flex-1">{REOPEN_PROMPT_LABEL}</span>
            </button>
          </li>
        )}
        {pending.map((prompt, i) => (
          <li key={prompt.action}>
            <button
              type="button"
              disabled={prompt.action === "edit_rules" && onEditRules === null}
              onClick={() => {
                if (prompt.action === "edit_rules") return onEditRules?.();
                return onShareDoor();
              }}
              className="nob-prompt-cta flex w-full items-center gap-3 rounded-2xl bg-accent
                         py-2 pl-2 pr-4 text-left text-sm font-semibold text-accent-fg
                         transition-colors duration-75 hover:bg-accent-soft active:bg-accent-strong
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-focus/70 focus-visible:ring-offset-2
                         focus-visible:ring-offset-bg disabled:pointer-events-none
                         disabled:opacity-40"
            >
              {/* The numeral is decoration, not the name of the button: hidden
                  from the accessible name so the row is still called what it
                  does. Sighted order comes from the list; anyone else gets it
                  from the `<ol>`. */}
              <span aria-hidden="true" className="nob-prompt-num">
                {i + 1}
              </span>
              <span className="min-w-0 flex-1">{PROMPT_BUTTON[prompt.action]}</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
